import React, { useState, useEffect, useRef, MutableRefObject } from "react";
import { useStaticQuery, graphql, withPrefix } from "gatsby";

import useBrowserMode from "../../../shared/hooks/useBrowserMode";
import { normalizeTwoWords } from "../../../shared/utils/stringUtilities";
import RecommenderModal from "../../organisms/recommender-modal/recommenderModal";
import Button from "../../atoms/button/button";
import SearcherInput from "./searcherInput";
import SearcherResults from "./searcherResults";

import "./searcher.scss";

interface SearcherInterface {
  compact?: boolean;
  type?: "comfama" | "preguntas" | "biblioteca" | "global";
  onSelect?: Function;
  scrollIntoQuestion?: Function;
  defaultPlaceholder?: string;
  noTabs?: boolean | undefined;
  autoFocus?: boolean | true;
  filterSelection?: Array<string>;
}

function translate(word: string) {
  switch (word) {
    case "audiobook":
      return "Audiolibro";
    case "book":
      return "Libro";
    case "document":
      return "Documento";
    case "film":
      return "Película";
    case "journal":
      return "Revista";
    case "podcast":
      return "Podcast";
    case "physical":
      return "físico";
    case "digital":
      return "digital";
    case "series":
      return "series";
    case "physical;digital":
      return "físico o digital";
    default:
      return word;
  }
}

const renderFormattedText = (text, searchWord) => {
  const searchWords = searchWord ? searchWord.toLowerCase().split(" ") : [];

  const formattedText =
    text && searchWords.length > 0
      ? text.split(" ").map((textWord, idx) =>
          !searchWords.includes(textWord.toLowerCase()) ? (
            <React.Fragment key={idx}>{textWord + " "}</React.Fragment>
          ) : (
            <React.Fragment key={idx}>
              <span>{textWord}</span>{" "}
            </React.Fragment>
          )
        )
      : text && text;

  return formattedText;
};

const Searcher = (props: SearcherInterface) => {
  const data = useStaticQuery(graphql`
    query SEARCHER_PLACEHOLDER {
      allContentfulHome(filter: { titulo: { eq: "Home bibliotecas" } }) {
        nodes {
          placeholdersBuscador
          referenciaContenido {
            titulo
            tituloGeneral
            tituloBoton
            textoRecomendador
            redireccionBoton
            referenciaTabsContenido {
              nombreTab
            }
          }
        }
      }
    }
  `);

  const {
    compact,
    type,
    scrollIntoQuestion,
    defaultPlaceholder,
    autoFocus,
    filterSelection,
  } = props;

  const node: MutableRefObject<any> = useRef();

  const [isActive, setActiveSelect] = useState(false);
  const [isRecommenderOpen, setIsRecommenderOpen] = useState(false);
  const [dynamicPlaceholder, setDynamicPlaceholder] = useState("");
  const [searchWord, setSearchWord] = useState("");
  const [filterSelected, setFilterSelected] = useState(["Todos"]);
  const [results, setResults] = useState([]);
  const { isBrowser, window, document } = useBrowserMode();

  useEffect(() => {
    if (filterSelection) {
      setFilterSelected(filterSelection);
    }

  }, [filterSelection])
  
  useEffect(() => {
    if (type === "comfama" || type === "biblioteca" || type === "global") {
      let cont = 0;

      const timer = setInterval(() => {
        if (cont > 2) cont = 0;
        setDynamicPlaceholder(`${placeholders[cont]}`);
        cont++;
      }, 1200);

      return () => clearInterval(timer);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isActive]);

  const placeholders = data.allContentfulHome.nodes[0].placeholdersBuscador;
  const {
    tituloGeneral,
    tituloBoton,
    textoRecomendador,
    redireccionBoton,
    referenciaTabsContenido,
  } = data.allContentfulHome.nodes[0].referenciaContenido.filter(
    (contenido: any) =>
      contenido.titulo === "Buscador bibliotecas > Opciones Materias"
  )[0];
  const handleClickOutside = (e: Event) => {
    if (node.current.contains(e.target)) {
      return;
    } else {
      setActiveSelect(false);
    }
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" && (type === "biblioteca" || type === "global")) {
      let contentTypes = "";
      filterSelected.map(
        (filter) => (contentTypes += `&contentType=${filter}`)
      );
      if (isBrowser && searchWord.trim().length > 2) {
        document.body.style.cursor = "wait";
        document.body.style.cursor = "default";
        window.location.assign(
            withPrefix(`/resultados-busqueda/?search=${searchWord}${contentTypes}`)
        );
      }
    }
    return;
  };

  const handleFilterSelection = (filter: string) => {
    let auxFilters = [...filterSelected];
    if (filter === "Todos" || filterSelected.includes("Todos")) {
      return setFilterSelected([filter]);
    }
    if (filterSelected.includes(filter)) {
      setFilterSelected(
        filterSelected.filter((filterSel) => filterSel !== filter)
      );
      auxFilters = filterSelected.filter((filterSel) => filterSel !== filter);
    } else {
      setFilterSelected([...filterSelected, filter]);
      auxFilters = [...filterSelected, filter];
    }
    if (auxFilters.length === 0) setFilterSelected(["Todos"]);
  };

  const inputFocus = () => {
    setSearchWord(`${searchWord} `)
    document.getElementById("inputSearch")?.focus();
  }
  
  return (
    <div
      className={`m-searcher-container ${
        compact ? "m-searcher-container--compact" : ""
      } ${
        type === "preguntas" ? "m-searcher-container--compact-extended" : ""
      }`}
    >
      <div
        ref={node}
        className={`m-searcher-container m-searcher-container__content ${
          isActive || searchWord.trim().length > 0
            ? "m-searcher-container__content--active"
            : ""
        }`}
      >
        <SearcherInput
          {...{
            isBrowser,
            searchWord,
            type,
            defaultPlaceholder,
            dynamicPlaceholder,
            setActiveSelect,
            setSearchWord,
            setResults,
            filterSelected,
            handleKeyDown,
            autoFocus,
          }}
        />
        {(searchWord.trim().length > 2 || !compact) && (
          <div className="m-searcher-container__separator" />
        )}
        {(searchWord.trim().length > 2 || !compact) && (
          <SearcherResults
            {...{
              compact,
              searchWord,
              results,
              onSelect: () => {},
              scrollIntoQuestion,
              setSearchWord,
              normalizeTwoWords,
              type,
              translate,
              renderFormattedText,
            }}
          />
        )}
        <div
          className={`m-searcher-container__filters ${
            isActive || searchWord.trim().length > 2
              ? "m-searcher-container__filters--active"
              : ""
          }`}
        >
          {!compact && (
            <span className="m-searcher-container__filters-title">
              {tituloGeneral}
            </span>
          )}
          <ul className="m-searcher-container__filters-body">
            {!props.noTabs &&
              referenciaTabsContenido.map((category, index) => (
                <li
                  className={`m-searcher-container__filters-item ${
                    filterSelected.includes(category.nombreTab)
                      ? "m-searcher-container__filters-item--selected"
                      : ""
                  }`}
                  key={`filter-${index}`}
                  onClick={() => {
                    handleFilterSelection(category.nombreTab);
                    inputFocus();
                  }}
                  id={`filter-${index}`}
                >
                  {category.nombreTab}
                </li>
              ))}
          </ul>
        </div>
      </div>
      {!compact && (
        <div className="m-searcher-container__recommender">
          <span className="m-searcher-container__recommender-label">
            {textoRecomendador}
          </span>
          <div className="m-prominent__button">
            <Button
              id="buttonProminent"
              type={"button"}
              classname={"secondary "}
              icon={redireccionBoton}
              text={tituloBoton}
              isDisabled={false}
              onClickAction={() => setIsRecommenderOpen(true)}
            />
            <Button
              id="buttonProminent"
              type={"link"}
              classname={"secondary "}
              icon={'icon-search'}
              text={'BÚSQUEDA AVANZADA'}
              isDisabled={false}
              href={'https://comfama.primo.exlibrisgroup.com/discovery/search?vid=57COMFAMA_INST:57COMFAMA&lang=es&mode=advanced'}
            />
          </div>
        </div>
      )}
      <RecommenderModal
        open={isRecommenderOpen}
        onClose={() => setIsRecommenderOpen(false)}
      />
    </div>
  );
};

export default Searcher;
